import React, { useMemo, useState } from "react";

import PageTitle from "../../../layouts/PageTitle";
import { useGetNcsDailyReportQuery } from "../../../../store/appApi";
import {
  usePartnerCode,
  useProjectPartners,
  panelClass,
  btnGhost,
  fmtDate,
  selectWhiteClass,
  WHITE_CONTROL_STYLE,
  WHITE_OPTION_STYLE,
} from "./config";
import { downloadWorkbook } from "../../../../services/netplusCustomerStatus";

const Opt = ({ value, children }) => (
  <option value={value} style={WHITE_OPTION_STYLE}>
    {children}
  </option>
);

// Movement metrics — the daily-report row keys behind each label. `MOVEMENTS`
// also drives the filter dropdown and the summary cards.
const MOVEMENTS = [
  {
    key: "newConnections",
    label: "New connections",
    strong: true,
    tone: "text-emerald-700",
  },
  { key: "fromInactive", label: "Inactive recharged", tone: "text-amber-700" },
  { key: "fromActive", label: "Active recharged", tone: "text-emerald-700" },
  { key: "fromSuspended", label: "Suspended recharged", tone: "text-rose-700" },
  { key: "lapsed", label: "New lapsed", tone: "text-amber-700" },
  { key: "churned", label: "Terminated", tone: "text-gray-500" },
];

const ALL_COLS = [
  { key: "date", label: "Date" },
  ...MOVEMENTS,
  { key: "totalRecharged", label: "Total recharged", strong: true },
];

const MONTH_FMT = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-IN", {
    month: "short",
    year: "numeric",
  });
};

const zeroRow = () => ({
  newConnections: 0,
  fromActive: 0,
  fromInactive: 0,
  fromSuspended: 0,
  totalRecharged: 0,
  lapsed: 0,
  churned: 0,
});

const addInto = (acc, r) => {
  Object.keys(zeroRow()).forEach((k) => {
    acc[k] = (acc[k] || 0) + (r[k] || 0);
  });
  return acc;
};

// ── Summary card ────────────────────────────────────────────────────────────
const Kpi = ({ label, value, tone }) => (
  <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-center">
    <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
      {label}
    </div>
    <div
      className={`mt-1 text-2xl font-semibold tabular-nums ${tone || "text-gray-900"}`}
    >
      {value ?? 0}
    </div>
  </div>
);

const SummaryCards = ({ title, subtitle, totals }) => (
  <section className={panelClass}>
    <div className="flex items-baseline justify-between border-b border-gray-100 px-5 py-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
        {title}
      </h2>
      {subtitle && <span className="text-xs text-gray-400">{subtitle}</span>}
    </div>
    <div className="grid grid-cols-2 gap-3 p-5 sm:grid-cols-3 lg:grid-cols-6">
      {MOVEMENTS.map((m) => (
        <Kpi
          key={m.key}
          label={m.label}
          value={totals[m.key] || 0}
          tone={m.tone}
        />
      ))}
    </div>
  </section>
);

const NcsDailyReport = () => {
  const [partnerCode, setPartnerCode] = usePartnerCode();
  const { codes: allPartnerCodes } = useProjectPartners();
  // Local to this screen only — never written to the shared partner
  // selection. Excel export needs one real partner, so it's hidden while
  // this is on; the scoreboard rows below combine every partner's numbers.
  const [viewAll, setViewAll] = useState(false);
  const { data, isFetching } = useGetNcsDailyReportQuery({
    partnerCode: viewAll ? "ALL" : partnerCode,
  });

  const rawRows = useMemo(() => data?.rows || [], [data]);

  // Filters
  const [groupBy, setGroupBy] = useState("date"); // "date" | "month"
  const [month, setMonth] = useState(""); // YYYY-MM or ""
  const [date, setDate] = useState(""); // YYYY-MM-DD or ""
  const [movement, setMovement] = useState(""); // MOVEMENTS[].key or ""

  const months = useMemo(
    () =>
      [...new Set(rawRows.map((r) => (r.date || "").slice(0, 7)))]
        .filter(Boolean)
        .sort()
        .reverse(),
    [rawRows],
  );
  const dates = useMemo(
    () =>
      [...new Set(rawRows.map((r) => r.date).filter(Boolean))].sort().reverse(),
    [rawRows],
  );

  // ── Yesterday + current-month summary (from the unfiltered data) ──────────
  const todayYmd = new Date().toISOString().slice(0, 10);
  const yesterdayYmd = new Date(Date.now() - 86400000)
    .toISOString()
    .slice(0, 10);
  const curMonth = todayYmd.slice(0, 7);

  const yesterdayRow =
    rawRows.find((r) => r.date === yesterdayYmd) ||
    // sheets aren't always daily — fall back to the latest day we have
    [...rawRows].sort((a, b) => b.date.localeCompare(a.date))[0];

  const monthTotals = useMemo(
    () =>
      rawRows
        .filter((r) => (r.date || "").startsWith(curMonth))
        .reduce((a, r) => addInto(a, r), zeroRow()),
    [rawRows, curMonth],
  );

  // ── Filtered / grouped table rows ───────────────────────────────────────
  const tableRows = useMemo(() => {
    let rows = rawRows;
    if (month) rows = rows.filter((r) => (r.date || "").startsWith(month));
    if (groupBy === "date" && date) rows = rows.filter((r) => r.date === date);

    if (groupBy === "month") {
      const byMonth = new Map();
      rows.forEach((r) => {
        const ym = (r.date || "").slice(0, 7);
        if (!ym) return;
        if (!byMonth.has(ym)) byMonth.set(ym, { date: ym, ...zeroRow() });
        addInto(byMonth.get(ym), r);
      });
      rows = [...byMonth.values()];
    }

    rows = [...rows].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    if (movement) rows = rows.filter((r) => (r[movement] || 0) > 0);
    return rows;
  }, [rawRows, month, date, groupBy, movement]);

  const cols = useMemo(() => {
    if (!movement) return ALL_COLS;
    const m = MOVEMENTS.find((x) => x.key === movement);
    return [ALL_COLS[0], m];
  }, [movement]);

  const totals = useMemo(
    () => tableRows.reduce((a, r) => addInto(a, r), zeroRow()),
    [tableRows],
  );

  const resetFilters = () => {
    setGroupBy("date");
    setMonth("");
    setDate("");
    setMovement("");
  };
  const filtersActive = groupBy !== "date" || month || date || movement;

  return (
    <>
      <PageTitle activeMenu="Daily Report" motherMenu="Netplus" />

      <div className="space-y-5">
        {/* ── Summary cards ─────────────────────────────────────────────── */}
        <div className="grid gap-5 lg:grid-cols-1">
          <SummaryCards
            title="Yesterday"
            subtitle={yesterdayRow ? fmtDate(yesterdayRow.date) : "—"}
            totals={yesterdayRow || zeroRow()}
          />
          <SummaryCards
            title={`This month — ${MONTH_FMT(curMonth)}`}
            subtitle="running total"
            totals={monthTotals}
          />
        </div>

        {/* ── Scoreboard + filters ─────────────────────────────────────── */}
        <section className={panelClass}>
          <div className="flex flex-wrap items-end gap-3 border-b border-gray-100 px-5 py-3">
            <h2 className="mr-auto text-sm font-semibold uppercase tracking-wide text-gray-500">
              Recharge scoreboard
            </h2>

            <div>
              <span className="mb-1 block text-[11px] uppercase tracking-wide text-gray-400">
                Group by
              </span>
              <select
                className={selectWhiteClass}
                style={WHITE_CONTROL_STYLE}
                value={groupBy}
                onChange={(e) => {
                  setGroupBy(e.target.value);
                  setDate("");
                }}
              >
                <Opt value="date">Date-wise</Opt>
                <Opt value="month">Month-wise</Opt>
              </select>
            </div>

            <div>
              <span className="mb-1 block text-[11px] uppercase tracking-wide text-gray-400">
                Month
              </span>
              <select
                className={selectWhiteClass}
                style={WHITE_CONTROL_STYLE}
                value={month}
                onChange={(e) => {
                  setMonth(e.target.value);
                  setDate("");
                }}
              >
                <Opt value="">All months</Opt>
                {months.map((m) => (
                  <Opt key={m} value={m}>
                    {MONTH_FMT(m)}
                  </Opt>
                ))}
              </select>
            </div>

            {groupBy === "date" && (
              <div>
                <span className="mb-1 block text-[11px] uppercase tracking-wide text-gray-400">
                  Date
                </span>
                <select
                  className={selectWhiteClass}
                  style={WHITE_CONTROL_STYLE}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                >
                  <Opt value="">All dates</Opt>
                  {dates
                    .filter((d) => !month || d.startsWith(month))
                    .map((d) => (
                      <Opt key={d} value={d}>
                        {fmtDate(d)}
                      </Opt>
                    ))}
                </select>
              </div>
            )}

            <div>
              <span className="mb-1 block text-[11px] uppercase tracking-wide text-gray-400">
                Movement
              </span>
              <select
                className={selectWhiteClass}
                style={WHITE_CONTROL_STYLE}
                value={movement}
                onChange={(e) => setMovement(e.target.value)}
              >
                <Opt value="">All movements</Opt>
                {MOVEMENTS.map((m) => (
                  <Opt key={m.key} value={m.key}>
                    {m.label}
                  </Opt>
                ))}
              </select>
            </div>

            {filtersActive && (
              <button className={btnGhost} onClick={resetFilters}>
                Clear
              </button>
            )}

            <div>
              <span className="mb-1 block text-[11px] uppercase tracking-wide text-gray-400">
                Partner
              </span>
              <select
                className={selectWhiteClass}
                style={WHITE_CONTROL_STYLE}
                value={viewAll ? "ALL" : partnerCode}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "ALL") {
                    setViewAll(true);
                  } else {
                    setViewAll(false);
                    setPartnerCode(v);
                  }
                }}
              >
                <Opt value="ALL">All partners</Opt>
                {(allPartnerCodes.length ? allPartnerCodes : [partnerCode]).map((c) => (
                  <Opt key={c} value={c}>
                    {c}
                  </Opt>
                ))}
              </select>
            </div>
            {!viewAll && (
              <button
                className={btnGhost}
                onClick={() => downloadWorkbook(partnerCode, { view: "daily" })}
              >
                Download Excel
              </button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                  {cols.map((c) => (
                    <th
                      key={c.key}
                      className={`px-4 py-3 ${
                        c.key === "date" ? "text-left" : "text-center"
                      }`}
                    >
                      {c.key === "date" && groupBy === "month"
                        ? "Month"
                        : c.label}
                    </th>
                  ))}
                  {!movement && (
                    <th className="px-4 py-3 text-center">Recovered via</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isFetching ? (
                  <tr>
                    <td
                      colSpan={cols.length + 1}
                      className="px-4 py-12 text-center text-gray-400"
                    >
                      Loading…
                    </td>
                  </tr>
                ) : tableRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={cols.length + 1}
                      className="px-4 py-12 text-center text-gray-400"
                    >
                      No rows for {viewAll ? "all partners" : partnerCode} with the
                      current filters.
                    </td>
                  </tr>
                ) : (
                  tableRows.map((r) => (
                    <tr key={r.date} className="hover:bg-cyan-50/30">
                      {cols.map((c) => (
                        <td
                          key={c.key}
                          className={`px-4 py-2.5 tabular-nums ${
                            c.key === "date"
                              ? "text-left font-medium text-gray-800"
                              : "text-center"
                          } ${
                            c.strong
                              ? "font-semibold text-emerald-700"
                              : "text-gray-600"
                          }`}
                        >
                          {c.key === "date"
                            ? groupBy === "month"
                              ? MONTH_FMT(r.date)
                              : fmtDate(r.date)
                            : (r[c.key] ?? 0)}
                        </td>
                      ))}
                      {!movement && (
                        <td className="px-4 py-2.5 text-center text-xs text-gray-400">
                          sheet {r.recoveredVia?.["sheet-detected"] ?? 0} · tc{" "}
                          {r.recoveredVia?.manual ?? 0} · portal{" "}
                          {r.recoveredVia?.["portal-payment"] ?? 0}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
              {tableRows.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold">
                    <td className="px-4 py-3 text-left text-xs uppercase tracking-wider text-gray-500">
                      Total
                    </td>
                    {cols.slice(1).map((c) => (
                      <td
                        key={c.key}
                        className="px-4 py-3 text-center tabular-nums"
                      >
                        {totals[c.key] || 0}
                      </td>
                    ))}
                    {!movement && <td />}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </section>
      </div>
    </>
  );
};

export default NcsDailyReport;
